"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="228703" className="hidden md:flex gap-8">
        <div className="h-4 w-16 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-4 w-20 bg-muted/20 animate-pulse rounded-sm" />
        <div className="h-4 w-18 bg-muted/20 animate-pulse rounded-sm" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-10">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-[18px] font-medium uppercase tracking-tight text-foreground/70 hover:text-primary transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 right-0 w-full max-w-full bg-background/98 backdrop-blur-xl border-b border-border shadow-2xl md:hidden">
      <nav className="container mx-auto px-6 py-10 flex flex-col gap-8">
        {loading ? <div className="flex flex-col gap-6">
            <div className="h-4 w-32 bg-muted/20 animate-pulse rounded-sm" />
            <div className="h-4 w-40 bg-muted/20 animate-pulse rounded-sm" />
            <div className="h-4 w-36 bg-muted/20 animate-pulse rounded-sm" />
          </div> : <>
            {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="font-default text-[18px] font-semibold uppercase tracking-tight text-foreground hover:text-primary transition-colors duration-200" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>)}
          </>}
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-[20px] border-b border-border py-2 md:py-4" : "bg-background py-4 md:py-8 border-b border-transparent"}`}>
      <MenuProvider menu_id="44" className="w-full">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between">
          
          <Link to="/" className="font-default text-[14px] md:text-[16px] font-medium uppercase tracking-tight text-foreground/70 hover:text-primary transition-colors duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
            Home
          </Link>

          
          <DesktopMenuConsumer />

          
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/10 hover:text-primary rounded-sm" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? "Close Menu" : "Open Menu"}>
            {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>

        
        <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </header>;
}